import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";

const STORAGE_KEY = id => `lhda_nt_users_${id}`;
const defaultData = { wings: [] };

function genId() {
  return Math.random().toString(36).slice(2, 9);
}

/* ── Simple name modal ── */
function NameModal({ title, placeholder, onConfirm, onCancel }) {
  const [name, setName] = useState("");
  return (
    <div style={S.overlay}>
      <div style={S.modal}>
        <h3 style={S.modalTitle}>{title}</h3>
        <input
          autoFocus
          style={S.modalInput}
          placeholder={placeholder}
          value={name}
          onChange={e => setName(e.target.value)}
          onKeyDown={e => {
            if (e.key === "Enter" && name.trim()) onConfirm(name.trim());
            if (e.key === "Escape") onCancel();
          }}
        />
        <div style={S.modalActions}>
          <button style={S.btnCancel} onClick={onCancel}>Cancel</button>
          <button style={S.btnPrimary} onClick={() => name.trim() && onConfirm(name.trim())}>Add</button>
        </div>
      </div>
    </div>
  );
}

/* ── Division modal (select wing) ── */
function DivisionModal({ wings, onConfirm, onCancel }) {
  const [wingId, setWingId] = useState(wings[0]?.id || "");
  const [name, setName] = useState("");
  return (
    <div style={S.overlay}>
      <div style={S.modal}>
        <h3 style={S.modalTitle}>New Division</h3>
        <label style={S.label}>Select Wing</label>
        <select style={S.select} value={wingId} onChange={e => setWingId(e.target.value)}>
          {wings.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
        </select>
        <label style={S.label}>Division Name</label>
        <input autoFocus style={S.modalInput} placeholder="Division name" value={name}
          onChange={e => setName(e.target.value)}
          onKeyDown={e => { if (e.key === "Enter" && name.trim()) onConfirm(wingId, name.trim()); if (e.key === "Escape") onCancel(); }} />
        <div style={S.modalActions}>
          <button style={S.btnCancel} onClick={onCancel}>Cancel</button>
          <button style={S.btnPrimary} onClick={() => name.trim() && onConfirm(wingId, name.trim())}>Add</button>
        </div>
      </div>
    </div>
  );
}

/* ── Branch modal (select wing → division) ── */
function BranchModal({ wings, onConfirm, onCancel }) {
  const [wingId, setWingId] = useState(wings[0]?.id || "");
  const wing = wings.find(w => w.id === wingId);
  const [divId, setDivId] = useState(wing?.divisions[0]?.id || "");

  useEffect(() => {
    const w = wings.find(w => w.id === wingId);
    setDivId(w?.divisions[0]?.id || "");
  }, [wingId, wings]);

  const [name, setName] = useState("");
  const divs = wing?.divisions || [];

  return (
    <div style={S.overlay}>
      <div style={S.modal}>
        <h3 style={S.modalTitle}>New Branch</h3>
        <label style={S.label}>Select Wing</label>
        <select style={S.select} value={wingId} onChange={e => setWingId(e.target.value)}>
          {wings.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
        </select>
        <label style={S.label}>Select Division</label>
        <select style={S.select} value={divId} onChange={e => setDivId(e.target.value)} disabled={!divs.length}>
          {divs.length ? divs.map(d => <option key={d.id} value={d.id}>{d.name}</option>) : <option>— no divisions —</option>}
        </select>
        <label style={S.label}>Branch Name</label>
        <input autoFocus style={S.modalInput} placeholder="Branch name" value={name}
          onChange={e => setName(e.target.value)}
          onKeyDown={e => { if (e.key === "Enter" && name.trim() && divId) onConfirm(wingId, divId, name.trim()); if (e.key === "Escape") onCancel(); }} />
        <div style={S.modalActions}>
          <button style={S.btnCancel} onClick={onCancel}>Cancel</button>
          <button style={S.btnPrimary} onClick={() => name.trim() && divId && onConfirm(wingId, divId, name.trim())}>Add</button>
        </div>
      </div>
    </div>
  );
}

/* ── Unit modal (select wing → division → branch) ── */
function UnitModal({ wings, onConfirm, onCancel }) {
  const [wingId, setWingId] = useState(wings[0]?.id || "");
  const wing = wings.find(w => w.id === wingId);
  const [divId, setDivId] = useState(wing?.divisions[0]?.id || "");
  const div = wing?.divisions.find(d => d.id === divId);
  const [branchId, setBranchId] = useState(div?.branches[0]?.id || "");
  const [name, setName] = useState("");

  useEffect(() => {
    const w = wings.find(w => w.id === wingId);
    const fd = w?.divisions[0];
    setDivId(fd?.id || "");
    setBranchId(fd?.branches[0]?.id || "");
  }, [wingId, wings]);

  useEffect(() => {
    const w = wings.find(w => w.id === wingId);
    const d = w?.divisions.find(d => d.id === divId);
    setBranchId(d?.branches[0]?.id || "");
  }, [divId, wingId, wings]);

  const divs = wing?.divisions || [];
  const branches = div?.branches || [];

  return (
    <div style={S.overlay}>
      <div style={S.modal}>
        <h3 style={S.modalTitle}>New Unit</h3>
        <label style={S.label}>Select Wing</label>
        <select style={S.select} value={wingId} onChange={e => setWingId(e.target.value)}>
          {wings.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
        </select>
        <label style={S.label}>Select Division</label>
        <select style={S.select} value={divId} onChange={e => setDivId(e.target.value)} disabled={!divs.length}>
          {divs.length ? divs.map(d => <option key={d.id} value={d.id}>{d.name}</option>) : <option>— no divisions —</option>}
        </select>
        <label style={S.label}>Select Branch</label>
        <select style={S.select} value={branchId} onChange={e => setBranchId(e.target.value)} disabled={!branches.length}>
          {branches.length ? branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>) : <option>— no branches —</option>}
        </select>
        <label style={S.label}>Unit Name</label>
        <input autoFocus style={S.modalInput} placeholder="Unit name" value={name}
          onChange={e => setName(e.target.value)}
          onKeyDown={e => { if (e.key === "Enter" && name.trim() && branchId) onConfirm(wingId, divId, branchId, name.trim()); if (e.key === "Escape") onCancel(); }} />
        <div style={S.modalActions}>
          <button style={S.btnCancel} onClick={onCancel}>Cancel</button>
          <button style={S.btnPrimary} onClick={() => name.trim() && branchId && onConfirm(wingId, divId, branchId, name.trim())}>Add</button>
        </div>
      </div>
    </div>
  );
}

/* ── Row in the user table ── */
function UserRow({ user, path, onDelete }) {
  return (
    <tr style={S.tr}>
      <td style={S.td}><div style={S.avatar}>{user.name[0].toUpperCase()}</div></td>
      <td style={S.td}>{user.name}</td>
      <td style={S.td}>{path.wing}</td>
      <td style={S.td}>{path.division}</td>
      <td style={S.td}>{path.branch}</td>
      <td style={S.td}>{path.unit}</td>
      <td style={S.td}>{user.role || "Member"}</td>
      <td style={{...S.td, textAlign:"center"}}>
        <button style={S.delRowBtn} onClick={onDelete}>Remove</button>
      </td>
    </tr>
  );
}

/* ── Main Page ── */
export default function NonTeachersUsersPage() {
  const { id } = useParams();
  const KEY = STORAGE_KEY(id);

  const [data, setData] = useState(() => {
    try { return JSON.parse(localStorage.getItem(KEY)) || defaultData; } catch { return defaultData; }
  });
  const [modal, setModal] = useState(null); // "wing"|"division"|"branch"|"unit"

  useEffect(() => {
    localStorage.setItem(KEY, JSON.stringify(data));
  }, [data, KEY]);

  /* ── mutators ── */
  const addWing = name =>
    setData(d => ({ wings: [...d.wings, { id: genId(), name, divisions: [] }] }));

  const addDivision = (wid, name) =>
    setData(d => ({ wings: d.wings.map(w => w.id !== wid ? w : { ...w, divisions: [...w.divisions, { id: genId(), name, branches: [] }] }) }));

  const addBranch = (wid, did, name) =>
    setData(d => ({ wings: d.wings.map(w => w.id !== wid ? w : { ...w, divisions: w.divisions.map(dv => dv.id !== did ? dv : { ...dv, branches: [...dv.branches, { id: genId(), name, units: [] }] }) }) }));

  const addUnit = (wid, did, bid, name) =>
    setData(d => ({ wings: d.wings.map(w => w.id !== wid ? w : { ...w, divisions: w.divisions.map(dv => dv.id !== did ? dv : { ...dv, branches: dv.branches.map(b => b.id !== bid ? b : { ...b, units: [...b.units, { id: genId(), name, members: [] }] }) }) }) }));

  const removeMember = (wid, did, bid, uid, mid) =>
    setData(d => ({ wings: d.wings.map(w => w.id !== wid ? w : { ...w, divisions: w.divisions.map(dv => dv.id !== did ? dv : { ...dv, branches: dv.branches.map(b => b.id !== bid ? b : { ...b, units: b.units.map(u => u.id !== uid ? u : { ...u, members: u.members.filter(m => m.id !== mid) }) }) }) }) }));

  /* ── flatten all members for the table ── */
  const rows = [];
  data.wings.forEach(w =>
    w.divisions.forEach(dv =>
      dv.branches.forEach(b =>
        b.units.forEach(u =>
          (u.members || []).forEach(m =>
            rows.push({ user: m, path: { wing: w.name, division: dv.name, branch: b.name, unit: u.name }, ids: { wid: w.id, did: dv.id, bid: b.id, uid: u.id } })
          )
        )
      )
    )
  );

  const noWings = data.wings.length === 0;
  const noDivs  = data.wings.every(w => w.divisions.length === 0);
  const noBranch = data.wings.every(w => w.divisions.every(d => d.branches.length === 0));
  const noUnits  = data.wings.every(w => w.divisions.every(d => d.branches.every(b => b.units.length === 0)));

  return (
    <div style={S.page}>
      <div style={S.header}>
        <h1 style={S.title}>Non-Teachers Users</h1>
        <p style={S.subtitle}>Unit {id} — Member Management</p>
      </div>

      {/* ── Action bar ── */}
      <div style={S.bar}>
        <button style={S.btnWing}   onClick={() => setModal("wing")}>Add Wing</button>
        <button style={{...S.btnDiv,    opacity: noWings  ? .45 : 1}} onClick={() => !noWings  && setModal("division")}>New Division</button>
        <button style={{...S.btnBranch, opacity: noDivs   ? .45 : 1}} onClick={() => !noDivs   && setModal("branch")}>New Branch</button>
        <button style={{...S.btnUnit,   opacity: noBranch ? .45 : 1}} onClick={() => !noBranch && setModal("unit")}>New Unit</button>
      </div>

      {/* ── Structure overview ── */}
      <div style={S.treeSection}>
        {noWings ? (
          <p style={S.hint}>Click <strong>Add Wing</strong> to start building the structure.</p>
        ) : (
          <div style={S.treeGrid}>
            {data.wings.map(w => (
              <div key={w.id} style={S.treeCard}>
                <div style={S.treeWing}>{w.name}</div>
                {w.divisions.map(dv => (
                  <div key={dv.id} style={S.treeDiv}>
                    <span style={S.treeDivLabel}>DIV</span> {dv.name}
                    {dv.branches.map(b => (
                      <div key={b.id} style={S.treeBranch}>
                        <span style={S.treeBranchLabel}>BR</span> {b.name}
                        {b.units.map(u => (
                          <div key={u.id} style={S.treeUnit}>
                            <span style={S.treeUnitLabel}>UNIT</span> {u.name}
                            <span style={S.treeMemberCount}>{(u.members||[]).length} members</span>
                          </div>
                        ))}
                        {!b.units.length && <div style={S.treeEmpty}>No units yet</div>}
                      </div>
                    ))}
                    {!dv.branches.length && <div style={S.treeEmpty}>No branches yet</div>}
                  </div>
                ))}
                {!w.divisions.length && <div style={S.treeEmpty}>No divisions yet</div>}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Members table ── */}
      {rows.length > 0 && (
        <div style={S.tableWrap}>
          <h2 style={S.tableTitle}>All Members</h2>
          <div style={{overflowX:"auto"}}>
            <table style={S.table}>
              <thead>
                <tr>{["","Name","Wing","Division","Branch","Unit","Role",""].map((h,i) => (
                  <th key={i} style={S.th}>{h}</th>
                ))}</tr>
              </thead>
              <tbody>
                {rows.map(({ user, path, ids }) => (
                  <UserRow
                    key={user.id}
                    user={user}
                    path={path}
                    onDelete={() => removeMember(ids.wid, ids.did, ids.bid, ids.uid, user.id)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Modals ── */}
      {modal === "wing" && (
        <NameModal title="Add Wing" placeholder="Wing name"
          onConfirm={name => { addWing(name); setModal(null); }}
          onCancel={() => setModal(null)} />
      )}
      {modal === "division" && (
        <DivisionModal wings={data.wings}
          onConfirm={(wid, name) => { addDivision(wid, name); setModal(null); }}
          onCancel={() => setModal(null)} />
      )}
      {modal === "branch" && (
        <BranchModal wings={data.wings}
          onConfirm={(wid, did, name) => { addBranch(wid, did, name); setModal(null); }}
          onCancel={() => setModal(null)} />
      )}
      {modal === "unit" && (
        <UnitModal wings={data.wings}
          onConfirm={(wid, did, bid, name) => { addUnit(wid, did, bid, name); setModal(null); }}
          onCancel={() => setModal(null)} />
      )}
    </div>
  );
}

/* ── Styles ── */
const S = {
  page: { minHeight: "100vh", background: "#f5f9f6", paddingBottom: "4rem" },
  header: { background: "linear-gradient(135deg,#1a4d28,#2878b5)", color: "#fff", padding: "3rem 2rem 2rem", textAlign: "center" },
  title: { fontFamily: "'Playfair Display',serif", fontSize: "2.2rem", marginBottom: ".4rem" },
  subtitle: { opacity: .8, fontSize: "1rem" },

  bar: { display: "flex", flexWrap: "wrap", gap: "1rem", padding: "1.5rem 2rem", alignItems: "center" },
  btnWing:   { padding: ".6rem 1.4rem", borderRadius: 8, border: "none", background: "#1a4d28", color: "#fff", fontWeight: 700, fontSize: ".9rem", cursor: "pointer" },
  btnDiv:    { padding: ".6rem 1.4rem", borderRadius: 8, border: "none", background: "#2878b5", color: "#fff", fontWeight: 700, fontSize: ".9rem", cursor: "pointer" },
  btnBranch: { padding: ".6rem 1.4rem", borderRadius: 8, border: "none", background: "#b07d2e", color: "#fff", fontWeight: 700, fontSize: ".9rem", cursor: "pointer" },
  btnUnit:   { padding: ".6rem 1.4rem", borderRadius: 8, border: "none", background: "#6b3fa0", color: "#fff", fontWeight: 700, fontSize: ".9rem", cursor: "pointer" },

  treeSection: { padding: "0 2rem 1.5rem", maxWidth: 1400, margin: "0 auto" },
  hint: { color: "#4e6058", fontSize: ".95rem", padding: "2rem 0" },
  treeGrid: { display: "flex", flexWrap: "wrap", gap: "1.5rem" },
  treeCard: { background: "#fff", border: "2px solid #1a4d28", borderRadius: 10, padding: "1rem 1.2rem", minWidth: 220, boxShadow: "0 2px 10px rgba(13,27,15,.08)" },
  treeWing: { fontWeight: 800, color: "#1a4d28", fontSize: "1rem", marginBottom: ".6rem", borderBottom: "1.5px solid #e0ede5", paddingBottom: ".4rem" },
  treeDiv: { marginLeft: ".8rem", marginBottom: ".4rem", color: "#1a5280", fontSize: ".88rem" },
  treeDivLabel: { fontSize: ".6rem", fontWeight: 800, background: "#2878b5", color: "#fff", padding: ".1rem .35rem", borderRadius: 3, marginRight: ".3rem" },
  treeBranch: { marginLeft: ".8rem", marginTop: ".3rem", color: "#7a4f00", fontSize: ".83rem" },
  treeBranchLabel: { fontSize: ".6rem", fontWeight: 800, background: "#b07d2e", color: "#fff", padding: ".1rem .35rem", borderRadius: 3, marginRight: ".3rem" },
  treeUnit: { marginLeft: ".8rem", marginTop: ".25rem", color: "#4a2080", fontSize: ".8rem", display: "flex", alignItems: "center", gap: ".5rem" },
  treeUnitLabel: { fontSize: ".58rem", fontWeight: 800, background: "#6b3fa0", color: "#fff", padding: ".1rem .3rem", borderRadius: 3 },
  treeMemberCount: { fontSize: ".68rem", color: "#888", marginLeft: "auto" },
  treeEmpty: { marginLeft: ".8rem", marginTop: ".2rem", color: "#aaa", fontSize: ".75rem", fontStyle: "italic" },

  tableWrap: { padding: "0 2rem", maxWidth: 1400, margin: "0 auto" },
  tableTitle: { fontFamily: "'Playfair Display',serif", fontSize: "1.3rem", color: "#1a4d28", marginBottom: "1rem" },
  table: { width: "100%", borderCollapse: "collapse", background: "#fff", borderRadius: 10, overflow: "hidden", boxShadow: "0 2px 12px rgba(13,27,15,.08)" },
  th: { background: "#1a4d28", color: "#fff", padding: ".65rem 1rem", textAlign: "left", fontSize: ".82rem", fontWeight: 700 },
  tr: { borderBottom: "1px solid #e8f0eb" },
  td: { padding: ".6rem 1rem", fontSize: ".88rem", color: "#2d3a30", verticalAlign: "middle" },
  avatar: { width: 32, height: 32, borderRadius: "50%", background: "#2878b5", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: ".9rem" },
  delRowBtn: { padding: ".25rem .7rem", fontSize: ".75rem", borderRadius: 5, border: "1.5px solid #c0392b", background: "#fff5f5", color: "#c0392b", fontWeight: 700, cursor: "pointer" },

  overlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 },
  modal: { background: "#fff", borderRadius: 12, padding: "2rem", width: "min(92vw,420px)", boxShadow: "0 14px 52px rgba(0,0,0,.25)" },
  modalTitle: { fontFamily: "'Playfair Display',serif", fontSize: "1.3rem", marginBottom: "1.2rem", color: "#1a4d28" },
  label: { display: "block", fontSize: ".8rem", fontWeight: 700, color: "#4e6058", marginBottom: ".35rem" },
  select: { width: "100%", padding: ".6rem .9rem", borderRadius: 7, border: "1.5px solid #ccddd2", fontSize: ".95rem", marginBottom: "1rem", background: "#fff" },
  modalInput: { width: "100%", padding: ".7rem 1rem", borderRadius: 8, border: "1.5px solid #ccddd2", fontSize: "1rem", marginBottom: "1.2rem", outline: "none" },
  modalActions: { display: "flex", justifyContent: "flex-end", gap: ".75rem" },
  btnCancel: { padding: ".55rem 1.2rem", borderRadius: 7, border: "1.5px solid #ccc", background: "#fff", color: "#4e6058", fontWeight: 600, cursor: "pointer" },
  btnPrimary: { padding: ".55rem 1.4rem", borderRadius: 7, border: "none", background: "#1a4d28", color: "#fff", fontWeight: 700, cursor: "pointer" },
};
